import Phaser from 'phaser';
import { Button } from './Button.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// Visual constants. Mirror DeleteAccountModal so destructive flows
// across the app feel visually consistent (same backdrop alpha,
// same depth bands, same radius).
const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;
const PANEL_DEPTH = 1001;
const PANEL_WIDTH = 560;
const PANEL_RADIUS = 12;

export interface ConfirmModalOptions {
  /** Bold, accent-colored title at the top of the panel. */
  readonly title: string;
  /** Body copy. `\n` is honored; auto-wraps inside the panel. */
  readonly body: string;
  /** Right-side action label. Defaults to "Confirm". */
  readonly confirmLabel?: string;
  /** Left-side action label. Defaults to "Cancel". */
  readonly cancelLabel?: string;
  /**
   * When true, paints the title + panel stroke in accent-red so the
   * modal visually reads as a warning. Also disables backdrop-tap
   * dismissal so a stray tap can't close the dialog — destructive
   * flows require an explicit Cancel.
   */
  readonly destructive?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Generic two-button confirm dialog. Lifecycle:
 *
 *   1. `new ConfirmModal(scene, opts)` to show.
 *   2. User taps Confirm or Cancel — the corresponding callback fires
 *      and the modal destroys itself.
 *
 * Modals are single-use. Re-instantiate to show again. Backdrop tap
 * cancels in non-destructive mode; in destructive mode the backdrop
 * absorbs taps but does nothing (forces an explicit Cancel).
 *
 * Auto-cleans on scene SHUTDOWN to avoid orphaned display objects if
 * the user navigates away while the modal is up.
 */
export class ConfirmModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: ConfirmModalOptions;
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly buttons: Button[] = [];
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: ConfirmModalOptions) {
    this.scene = scene;
    this.opts = opts;

    this.drawBackdrop();
    this.drawPanel();
    this.drawTitle();
    this.drawBody();
    this.drawButtons();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy();
    });
  }

  private drawBackdrop(): void {
    const bg = this.scene.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        0x000000,
        BACKDROP_ALPHA
      )
      .setDepth(BACKDROP_DEPTH)
      .setInteractive();
    if (!this.opts.destructive) {
      // Non-destructive flow: backdrop tap cancels. Destructive flows
      // require an explicit Cancel tap (matches DeleteAccountModal).
      bg.on('pointerdown', () => this.handleCancel());
    }
    this.objects.push(bg);
  }

  private drawPanel(): void {
    const panelHeight = 360;
    const left = DESIGN_WIDTH / 2 - PANEL_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - panelHeight / 2;

    const panel = this.scene.add.graphics().setDepth(PANEL_DEPTH);
    panel.fillStyle(COLORS.bgDeepInt, 1);
    panel.fillRoundedRect(left, top, PANEL_WIDTH, panelHeight, PANEL_RADIUS);
    panel.lineStyle(2, this.accentInt(), 0.95);
    panel.strokeRoundedRect(left, top, PANEL_WIDTH, panelHeight, PANEL_RADIUS);
    this.objects.push(panel);
  }

  private drawTitle(): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 120, this.opts.title, {
        fontFamily: FONT_HEADER,
        fontSize: '26px',
        color: this.accentHex(),
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH + 1);
    this.objects.push(t);
  }

  private drawBody(): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 40, this.opts.body, {
        fontFamily: FONT_MONO,
        fontSize: '16px',
        color: COLORS.textPrimary,
        align: 'center',
        wordWrap: { width: PANEL_WIDTH - 80 },
      })
      .setLineSpacing(4)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH + 1);
    this.objects.push(t);
  }

  private drawButtons(): void {
    const y = DESIGN_HEIGHT / 2 + 100;
    const cancel = new Button(this.scene, {
      x: DESIGN_WIDTH / 2 - 130,
      y,
      width: 220,
      height: 64,
      text: this.opts.cancelLabel ?? 'Cancel',
      onTap: () => this.handleCancel(),
    });
    cancel.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(cancel);

    const confirm = new Button(this.scene, {
      x: DESIGN_WIDTH / 2 + 130,
      y,
      width: 220,
      height: 64,
      text: this.opts.confirmLabel ?? 'Confirm',
      style: 'primary',
      onTap: () => this.handleConfirm(),
    });
    confirm.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(confirm);
  }

  private handleConfirm(): void {
    if (this.destroyed) return;
    this.opts.onConfirm();
    this.destroy();
  }

  private handleCancel(): void {
    if (this.destroyed) return;
    this.opts.onCancel();
    this.destroy();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
    for (const b of this.buttons) b.destroy();
    this.buttons.length = 0;
  }

  private accentInt(): number {
    return this.opts.destructive
      ? COLORS.accentRedInt
      : COLORS.accentCyanInt;
  }

  private accentHex(): string {
    return this.opts.destructive
      ? COLORS.accentRedHex
      : COLORS.accentCyanHex;
  }
}
